import { useState, useMemo, useCallback, useEffect, useRef, useContext } from 'react';
import { Button, Icon, Popup }                                           from 'semantic-ui-react';
import styled                                                            from 'styled-components';
import PassFailResult                                                    from '../../../components/PassFailResult';
import DetailsSegment                                                    from '../../../components/DetailsSegment';
import CenterCell                                                        from '../../../components/table/CenterCell';
import { getAPI, postAPI }                                               from '../../../libs/fetchAPI';
import Duration                                                          from '../../../components/Duration';
import SingleDetailsModal                                                from '../../../components/SingleDetailsModal';
import ServerPaginatedTable                                              from '../../../components/table/ServerPaginatedTable';
import AppContext                                                        from '../../../context/HeaderContext';
import RightCell                                                         from '../../../components/table/RightCell';
import IntroMessage                                                      from '../../../components/IntroMessage';
import JiraLink                                                          from '../../../components/JiraLink';
import FlexBox                                                           from '../../../components/FlexBox';

const CustomFlexBox = styled(FlexBox)`
	justify-content: space-between;
`;

function Spec({ handleSetSpecName }) {
	const { current_project_id, user }                = useContext(AppContext);
	const [ loading, setLoading ]                     = useState(false);
	const [ loading_disable, setLoadingDisable ]      = useState(false);
	const [ loading_update, setLoadingUpdate ]        = useState(false);
	const [ loading_details, setLoadingDetails ]      = useState({});
	const [ spec_info, setSpecInfo ]                  = useState([]);
	const [ suites, setSuites ]                       = useState([]);
	const [ tests, setTests ]                         = useState([]);
	const [ test_errors, setTestErrors ]              = useState([]);
	const [ show_details_modal, setShowDetailsModal ] = useState(false);
	const [ page_count, setPageCount ]                = useState();
	const [ is_disabled, setIsDisabled ]              = useState(false);
	const [ is_updated, setIsUpdated ]                = useState(false);
	const [ hidden_runs, setHiddenRuns ]              = useState(0);
	const fetch_id_ref                                = useRef(0);

	/* eslint-disable */
	useEffect(() => {
		const param_query = new URLSearchParams(window.location.search);

		const params = {
			spec_file  : param_query.get(`spec_file`),
			version    : param_query.get(`version`),
			project_id : current_project_id,
		};

		const error_params = { ...params, ...{
			sort_key  : `run_date`,
			limit     : 100,
		}};

		Promise.all([
			getAPI(`/general_suite_info?${new URLSearchParams(params)}`),
			getAPI(`/distinct_runs?${new URLSearchParams({ project_id: current_project_id, limit: 20000 })}`)
		]).then((response) => {

				let suite         = `N/A`;
				const info        = response[0].data[0];
				const runs        = [...new Set(response[1].data.map((run) => run.title))];
				const recent_runs = runs.map((run_title) => {
					return {
						key   : run_title,
						value : run_title,
						text  : run_title,
					};
				});

				recent_runs.unshift({ key: ``, value: ``, text: `` });

				if(!info) {
					return;
				}

				handleSetSpecName(info.spec_file);

				if(info.filepath) {
					const tmp = info.filepath.split(`/`);
					suite     = tmp.includes(`modules`) ? tmp[3] : tmp[1];
				}

				const tmp_suite_info = [
					{
						header : `Spec File`,
						title  : <JiraLink issue_key={info.spec_file} />
					},
					{
						header : `Suite Title`,
						title  : info.title,
					},
					{
						header : `Version`,
						title  : info.version || `N/A`
					},
					{
						header : `Suite`,
						title  : suite,
						info   : info.filepath
					},
					{
						header : `Hidden Runs`,
						title  : hidden_runs ? <a href="#">Hide</a> : <a href="#">Show</a>,
						handler : handleToggleHiddenRuns,
					}
				];

				if(user.admin && current_project_id === 2) {
					tmp_suite_info.push({
						header  : `Disable Test`,
						title   : is_disabled || info.disabled ? <Icon name="cancel" /> : (!loading_disable ? <a href="#">Disable</a> : <Icon name="spinner" />),
						handler : handleDisableTest,
						params  : { spec_file : info.spec_file, version : info.version },
					});

					tmp_suite_info.push({
						header  : `Improve Test`,
						title   : is_updated ? <Icon name="cancel" /> : (!loading_update ? <a href="#">Update</a> : <Icon name="spinner" />),
						handler : handleUpdateTest,
						params  : { spec_file : info.spec_file, version : info.version },
					});
				}

				setSpecInfo(tmp_suite_info);
			});
	}, [is_disabled]);
	/* eslint-enable */

	function handleToggleHiddenRuns() {
		console.log(`foo`);
		setHiddenRuns(prev_state => !prev_state);
	}

	async function handleDisableTest({ spec_file, version }) {
		setLoadingDisable(true);

		await postAPI(`/suite_info/modify?${new URLSearchParams({ spec_file, version, action: `disable` })}`);

		setLoadingDisable(false);
		setIsDisabled(true);
	}

	async function handleUpdateTest({ spec_file, version }) {
		setLoadingUpdate(true);

		await postAPI(`/suite_info/modify?${new URLSearchParams({ spec_file, version, action: `update` })}`);

		setLoadingUpdate(false);
		setIsUpdated(true);
	}

	function handleCloseDetails() {
		setShowDetailsModal(false);
	}

	function handleViewDetails(id) {
		setLoadingDetails({ [id] : true });

		Promise.all([
			getAPI(`/suites/${id}/tests`),
			getAPI(`/suites/${id}/errors`)
		]).then((response) => {
				const [ tmp_tests, tmp_errors ] = response;

				setTests(tmp_tests.data.reverse());
				setTestErrors(tmp_errors.data);
				setShowDetailsModal(true);
				setLoadingDetails({});
			});
	}

	/* eslint-disable */
	const fetchTableData = useCallback(({ pageIndex, pageSize, filters, sortBy }) => {
		const fetch_id = ++fetch_id_ref.current;

		if(fetch_id !== fetch_id_ref.current) {
			return;
		}

		let sort_key       = `run_date`;
		let sort_direction = `DESC`;

		if(sortBy.length) {
			sort_key       = sortBy[0].id;
			sort_direction = sortBy[0].desc ? `DESC` : `ASC`;
		}

		const params = {
			project_id     : current_project_id,
			limit          : pageSize,
			offset         : pageSize * pageIndex,
			filters        : JSON.stringify(filters),
			spec_file      : new URLSearchParams(window.location.search).get(`spec_file`),
			version        : new URLSearchParams(window.location.search).get(`version`),
			hidden_runs    : 1,
			sort_key,
			sort_direction,
		};

		setLoading(true);
		getAPI(`/suite_info_single?${new URLSearchParams(params)}`)
			.then(({ data, pages }) => {
				setSuites(data);
				setPageCount(pages);
				setLoading(false);
			});
	}, [current_project_id, hidden_runs]);
	/* eslint-enable */

	const columns = useMemo(() => [
		{
			Header   : `Build Key`,
			accessor : `run_name`,
			Cell     : ({ cell }) => (
				<CustomFlexBox>
					<JiraLink issue_key={cell.value} />&nbsp;&nbsp;
					<Popup content={cell.row.values.issue_summary} trigger={<Icon name="info circle" color="teal" />} />
				</CustomFlexBox>
			)
		},
		{
			Header   : `Build Date`,
			accessor : `run_date`,
			Cell     : ({ cell }) => <span>{new Date(`${cell.value} UTC`).toLocaleString(`en-US`, {
				month  : `2-digit`,
				day    : `2-digit`,
				hour   : `2-digit`,
				minute : `2-digit`,
				hour12 : true,
			})}</span>
		},
		{
			Header   : `User`,
			accessor : `issue_user`,
			collapse : true,
		},
		{
			Header    : `Spec Start`,
			accessor  : `start`,
			Cell      : ({ cell }) => <span>{cell.value ? new Date(`${cell.value} UTC`).toLocaleString() : ``}</span>
		},
		{
			Header   : `Capabilities`,
			accessor : `capabilities`,
		},
		{
			Header   : `Retries`,
			accessor : `retries`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Duration`,
			accessor : `duration`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell><Duration duration={cell.value} /></RightCell>
		},
		{
			Header   : `Result`,
			accessor : `passed`,
			collapse : true,
			Cell     : ({ cell }) => <PassFailResult passed={cell.value} skipped={cell.row.values.skipped} />
		},
		{
			Header   : `Skipped Hidden`,
			accessor : `skipped`,
		},
		{
			Header   : `Issue Summary`,
			accessor : `issue_summary`,
		},
		{
			Header   : ``,
			accessor : `id`,
			collapse : true,
			Cell     : ({ cell }) => <CenterCell><Button loading={loading_details[cell.value]} color="teal" onClick={() => handleViewDetails(cell.value)}>Details</Button></CenterCell>
		}
	], [loading_details]);

	/*
	if(!id || id === `none`) {
		return <Message>No suite selected</Message>
	}
	*/

	return (
		<>
			<SingleDetailsModal
				open={show_details_modal}
				handleClose={handleCloseDetails}
				tests={tests}
				errors={test_errors}
			/>

			<IntroMessage
				id={4}
				message='The Spec History -> Selected Suite page lists the history of an individual spec for each test run.'
			/>

			<DetailsSegment
				details={spec_info}
				// loading={segment_loading}
			/>

			<ServerPaginatedTable
				columns={columns}
				data={suites}
				fetchTableData={fetchTableData}
				loading={loading}
				pageCount={page_count}
				hidden_runs={hidden_runs}
				default_page_size="10"
				styles={{ position : `relative` }}
				hidden_columns={[`skipped`, `issue_summary`]}
			/>
		</>
	);
}

export default Spec;
